<template>
  <div class="ppt-main">
    <div v-if="!readonly && entitlement.showInviteProgress" class="invite-progress-panel">
      <div class="invite-progress-head">
        <span class="invite-progress-title">邀请进度</span>
        <span class="invite-progress-meta">已邀请 {{ entitlement.inviteCount }} 人 · 剩余免费次数 {{ entitlement.remainingCount }}</span>
      </div>
      <div class="invite-progress-cells">
        <span
          v-for="n in 5"
          :key="n"
          class="invite-cell"
          :class="{ lit: n <= entitlement.inviteProgressFilled }"></span>
      </div>
      <p class="invite-progress-tip">
        <template v-if="entitlement.remainingCount > 0 && entitlement.invitesNeededForNext > 0">
          再邀请 {{ entitlement.invitesNeededForNext }} 人凑满 5 格，可多获得 1 次生成机会（每 5 人 1 次，不扣积分）
        </template>
        <template v-else-if="!entitlement.canSubmit">
          {{ entitlement.blockMessage }}
          <router-link to="/workbench/invite" class="invite-link">去邀请好友</router-link>
        </template>
        <template v-else>
          每邀请 5 位好友注册，额外获得 1 次免费生成；当前还可免费生成 {{ entitlement.remainingCount }} 次
        </template>
      </p>
    </div>

    <div class="ppt-workspace">
      <div class="form-panel">
        <div class="panel-head">
          <p class="panel-tip">
            上传 docx、doc、pdf、txt、md（单文件 ≤10MB），按场景选模板，一键生成可编辑 pptx。
            适用<strong>开题/中期/毕业答辩</strong>、课程与课题汇报、<strong>工作总结、研究报告</strong>、项目复盘、培训与内部分享等；可选演讲备注与自动配图，约 3–8 分钟。
          </p>
        </div>

        <div class="compact-form-row">
          <span class="compact-label">场景快选</span>
          <div class="compact-body">
            <div class="type-row">
              <label v-for="item in pptTypes" :key="item.value" class="type-chip" :class="{ active: pptType === item.value }">
                <input v-model="pptType" type="radio" :value="item.value" :disabled="readonly" @change="onPptTypeChange">
                <span>{{ item.label }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="compact-form-row">
          <span class="compact-label">篇幅</span>
          <div class="compact-body">
            <div class="length-row">
              <label v-for="item in pptLengthOptions" :key="item.value" class="length-chip" :class="{ active: pptLength === item.value }">
                <input v-model="pptLength" type="radio" :value="item.value" :disabled="readonly">
                <span>{{ item.label }}</span>
              </label>
            </div>
          </div>
        </div>

        <p class="type-row-hint">场景快选会同步模板分类与风格；最终以模板区所选为准。</p>

        <div
            class="upload-zone"
            :class="{ dragover: dragOver, uploading: uploading, readonly: readonly }"
            @click="pickFile"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop">
          <input ref="fileInput" type="file" class="file-input" accept=".docx,.doc,.pdf,.txt,.md" :disabled="readonly || uploading" @change="onFileChange">
          <div v-if="uploading" class="upload-loading">
            <div class="loading-spinner"></div>
            <p class="upload-loading-title">正在上传文档…</p>
            <p v-if="uploadingFileName" class="upload-loading-name">{{ uploadingFileName }}</p>
          </div>
          <template v-else>
            <i class="bi bi-cloud-upload upload-icon"></i>
            <p class="upload-title">拖拽或 <span class="upload-link">点击上传</span> 文档</p>
            <p class="upload-hint">docx / doc / pdf / txt / md</p>
          </template>
        </div>

        <div v-if="uploadSession" class="upload-info">
          <div class="file-row">
            <i class="bi bi-file-earmark-text"></i>
            <div class="file-meta">
              <div class="file-name">{{ uploadSession.sourceFileName }}</div>
              <div v-if="uploadQuotaHint" class="file-sub">{{ uploadQuotaHint }}</div>
            </div>
            <button type="button" class="link-btn" @click="removeUpload">移除</button>
          </div>
        </div>

        <div class="form-fields">
          <div class="form-row-duo">
            <div class="form-row form-row-nested">
              <label class="form-label">标题</label>
              <input v-model="docTitle" type="text" maxlength="50" placeholder="课题标题（选填）" :disabled="readonly">
            </div>
            <div class="form-row form-row-nested">
              <label class="form-label">作者</label>
              <input v-model="docAuthor" type="text" maxlength="50" placeholder="作者姓名（选填）" :disabled="readonly">
            </div>
          </div>
          <div class="form-row template-section">
            <label class="form-label">模板</label>
            <div class="template-panel">
              <div class="template-filters">
                <select v-model="templateCategory" :disabled="readonly || templatesLoading" @change="onTemplateFilterChange">
                  <option v-for="c in categoryOptions" :key="c" :value="c">{{ c }}</option>
                </select>
                <select v-model="templateStyle" :disabled="readonly || templatesLoading" @change="onTemplateFilterChange">
                  <option v-for="s in styleOptions" :key="s" :value="s">{{ s }}</option>
                </select>
                <button type="button" class="ghost-mini" :disabled="templatesLoading" @click="loadTemplates">
                  <i class="bi bi-arrow-clockwise" :class="{ spinning: templatesLoading }"></i>
                </button>
              </div>
              <p v-if="!templatesLoading && !templates.length" class="template-empty-hint">当前分类下暂无模板，请切换分类、风格或点击刷新。</p>
              <div v-else class="template-grid">
                <button
                  v-for="tpl in templates"
                  :key="String(tpl.templateId)"
                  type="button"
                  class="tpl-card"
                  :class="{ active: isSameTemplateId(tpl.templateId, templateId) }"
                  :disabled="readonly"
                  @click="selectTemplate(tpl)">
                  <img v-if="tpl.coverImage" :src="tpl.coverImage" alt="" class="tpl-cover" loading="lazy">
                  <span v-else class="tpl-random"><i class="bi bi-easel"></i></span>
                  <span class="tpl-name">{{ tpl.templateName || tpl.style }}</span>
                  <span class="tpl-meta">{{ tpl.style }} · {{ tpl.color || tpl.industry }}</span>
                </button>
              </div>
            </div>
          </div>
          <div class="compact-form-row option-compact">
            <span class="compact-label">增强</span>
            <div class="compact-body option-row">
              <label class="check-item">
                <input v-model="isCardNote" type="checkbox" :disabled="readonly">
                <span>生成演讲备注</span>
              </label>
              <label class="check-item">
                <input v-model="isFigure" type="checkbox" :disabled="readonly">
                <span>自动配图</span>
              </label>
            </div>
          </div>
        </div>

        <div class="submit-row" :class="{ 'submit-row-dual': entitlement.showPointsPayOption }">
          <button
            v-if="entitlement.showPointsPayOption"
            type="button"
            class="points-pay-btn"
            :disabled="submitting"
            @click="handleSubmit(true)">
            {{ submitting ? '提交中…' : `扣除 ${entitlement.pointsPayCost} 积分生成` }}
          </button>
          <button
            type="button"
            class="primary-btn"
            :disabled="submitting"
            @click="handleSubmit(false)">
            {{ submitButtonLabel }}
          </button>
        </div>
        <p v-if="formBlockedHint" class="submit-hint-text">{{ formBlockedHint }}</p>
        <p v-if="entitlement.showPointsPayOption && !entitlement.canPayWithPoints" class="submit-hint-text">
          积分不足时可去充值，或使用右侧「免费生成PPT」（邀请好友凑次数）
        </p>
        <p v-if="!canSubmit && !inviteUnlockOnly && entitlement.blockMessage" class="warn-text">{{ entitlement.blockMessage }}</p>
        <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
      </div>

      <aside class="info-panel">
        <div class="notice-box">
          <div class="notice-head"><i class="bi bi-info-circle-fill"></i><span>使用说明</span></div>
          <p class="guide-lead">
            本功能会读取您上传的文档正文，结合所选类型、模板与篇幅，由 AI 生成可下载的 .pptx 文件。生成约 3–8 分钟，进度见下方「生成记录」。
          </p>
          <div v-if="!readonly && entitlement.showInviteProgress" class="guide-block rules-block">
            <h4 class="guide-title"><i class="bi bi-gift"></i> 免费规则</h4>
            <ul class="guide-list">
              <li>您的<strong>首次免费</strong>已使用；之后想再生成，需要邀请好友注册来换取次数（不扣积分）。</li>
              <li><strong>5 位好友 = 多 1 次</strong>：每凑满 5 名有效邀请（好友通过您的链接首次注册），就多 1 次免费生成机会。</li>
              <li><strong>看页顶 5 格进度</strong>：每成功邀请 1 人亮 1 格，亮满 5 格即多出 1 次可用次数。</li>
              <li><strong>举例</strong>：已邀 6 人 → 除首次外还能再免费做 <strong>1</strong> 次，第 6 人只算下一批的 1 格；已邀 10 人 → 除首次外还能再做 <strong>2</strong> 次。</li>
              <li>只有任务<strong>生成成功</strong>才算用掉 1 次；失败不占次数，改好文档可重新提交。</li>
              <li>也可选择<strong>扣除 {{ entitlement.pointsPayCost || 5000 }} 积分</strong>直接生成，不占用邀请次数。</li>
            </ul>
          </div>
          <ol class="guide-steps">
            <li><strong>场景快选（可选）</strong>：顶部按钮可快速切换模板分类与风格；也可直接在模板区用下拉框选择，以模板区为准。</li>
            <li><strong>上传文档</strong>：支持 docx、doc、pdf、txt、md，单文件不超过10MB。内容越完整、结构越清晰，生成效果越好；请勿上传与课题无关或空白文档。</li>
            <li><strong>填写信息（选填）</strong>：标题会优先作为PPT封面主题；作者会写入元信息。未填写时系统会从文件名推断标题。</li>
            <li><strong>挑选模板</strong>：在分类、风格下拉框中可微调筛选，点击封面卡片选中具体模板（必选一项）。开题、中期在平台上对应「毕业答辩」类模板资源。</li>
            <li><strong>篇幅与增强</strong>：「精简 / 标准 / 详细」控制页数大致多少；默认开启「演讲备注」与「自动配图」，便于答辩展示。</li>
            <li><strong>提交与下载</strong>：点击「免费生成PPT」。完成后在记录中下载，建议本地核对后再答辩。</li>
          </ol>
        </div>
        <div class="guide-block">
          <h4 class="guide-title"><i class="bi bi-exclamation-triangle"></i> 温馨提示</h4>
          <ul class="guide-list guide-list-tips">
            <li>内容严格依赖上传文档，不会编造实验数据。</li>
            <li>生成失败不占用免费次数，可修改后重试。</li>
            <li v-if="entitlement.showInviteProgress">邀请以好友首次注册并绑定您的邀请码为准，去「邀请有奖」复制链接。</li>
            <li v-else>每位新用户首次生成PPT免费，不扣积分。</li>
          </ul>
        </div>
      </aside>
    </div>

    <div class="table-panel">
      <div class="table-head">
        <h3>生成记录</h3>
        <button type="button" class="ghost-btn" :disabled="recordsLoading" @click="loadRecords">
          <i class="bi bi-arrow-clockwise" :class="{ spinning: recordsLoading }"></i>
        </button>
      </div>

      <div v-if="!readonly && records.length" class="record-table">
        <div v-for="item in records" :key="item.taskNo" class="table-row">
          <div class="item type">
            <div class="item-label">类型</div>
            <div class="item-value">{{ item.pptTypeLabel || '-' }}</div>
          </div>
          <div class="item title">
            <div class="item-label">标题</div>
            <div class="item-value title-text">{{ displayTitle(item) }}</div>
          </div>
          <div class="item status">
            <div class="item-label">状态</div>
            <div class="item-value">
              <span v-if="isProcessing(item)" class="tag tag-loading">生成中 {{ item.progress || 0 }}%</span>
              <span v-else-if="item.status === '2'" class="tag tag-done">已完成</span>
              <span v-else-if="item.status === '3'" class="tag tag-fail" :title="item.errorMsg">失败</span>
              <span v-else class="tag tag-queue">排队中</span>
            </div>
          </div>
          <div class="item pages">
            <div class="item-label">页数</div>
            <div class="item-value">{{ item.status === '2' && item.totalPages ? item.totalPages : '-' }}</div>
          </div>
          <div class="item date">
            <div class="item-label">提交时间</div>
            <div class="item-value">{{ item.createTime || '-' }}</div>
          </div>
          <div class="item operate">
            <div class="item-label">操作</div>
            <div class="item-value op-links">
              <button type="button" class="op-link" :disabled="item.status !== '2' || downloadingTaskNo === item.taskNo" @click="downloadPpt(item)">
                <i class="bi bi-download"></i>{{ downloadingTaskNo === item.taskNo ? '获取中…' : '下载 PPT' }}
              </button>
              <button type="button" class="op-link danger" @click="deleteRecord(item)">
                <i class="bi bi-trash"></i>删除
              </button>
            </div>
          </div>
        </div>
      </div>

      <p v-else-if="readonly" class="empty-text">登录后查看生成记录</p>
      <p v-else-if="recordsLoading" class="empty-text">加载中…</p>
      <p v-else class="empty-text">暂无记录，上传文档后开始生成</p>
    </div>
  </div>
</template>

<script>
import {
  getPptEntitlement,
  uploadPptFile,
  deletePptUpload,
  getPptTemplates,
  getPptTemplateOptions,
  submitPpt,
  getPptTasks,
  getPptDownloadUrl,
  deletePptRecord
} from '@/api/portal/ppt'

const POLL_INTERVAL = 4000

const PPT_TYPES = [
  { value: 'OPENING', label: '开题答辩' },
  { value: 'MIDTERM', label: '中期答辩' },
  { value: 'GRADUATION', label: '毕业答辩' },
  { value: 'WORK_SUMMARY', label: '工作总结' },
  { value: 'RESEARCH_REPORT', label: '研究报告' }
]

/** 切换答辩/报告类型时同步模板分类与风格（分类请求后端会映射到文多多） */
const TYPE_TEMPLATE_PRESETS = {
  OPENING: { category: '开题答辩', style: '扁平简约' },
  MIDTERM: { category: '中期答辩', style: '扁平简约' },
  GRADUATION: { category: '毕业答辩', style: '扁平简约' },
  WORK_SUMMARY: { category: '工作总结', style: '商务科技' },
  RESEARCH_REPORT: { category: '研究报告', style: '扁平简约' }
}

const PORTAL_CATEGORIES = ['开题答辩', '中期答辩', '毕业答辩', '工作总结', '研究报告', '教育培训', '商业计划']
const PORTAL_STYLES = ['扁平简约', '商务科技', '文艺清新', '中国风', '创意时尚']

const CATEGORY_TO_PPT_TYPE = {
  开题答辩: 'OPENING',
  中期答辩: 'MIDTERM',
  毕业答辩: 'GRADUATION',
  工作总结: 'WORK_SUMMARY',
  研究报告: 'RESEARCH_REPORT'
}

const PPT_LENGTH_OPTIONS = [
  { value: 'short', label: '精简' },
  { value: 'medium', label: '标准' },
  { value: 'long', label: '详细' }
]

function normalizeTemplateId(id) {
  if (id === null || id === undefined) {
    return ''
  }
  return String(id).trim()
}

function buildRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'pt-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10)
}

const EMPTY_ENTITLEMENT = {
  successCount: 0,
  inviteCount: 0,
  totalAllowed: 1,
  remainingCount: 1,
  firstFreeAvailable: true,
  showInviteProgress: false,
  inviteProgressFilled: 0,
  invitesNeededForNext: 5,
  canSubmit: true,
  blockMessage: '',
  nextQuotaCost: 0,
  showPointsPayOption: false,
  pointsPayCost: 5000,
  canPayWithPoints: false
}

export default {
  name: 'PptView',
  props: {
    readonly: { type: Boolean, default: false },
    balance: { type: Number, default: 0 }
  },
  data() {
    return {
      pptTypes: PPT_TYPES,
      pptLengthOptions: PPT_LENGTH_OPTIONS,
      pptType: 'OPENING',
      dragOver: false,
      uploading: false,
      uploadingFileName: '',
      submitting: false,
      uploadSession: null,
      docTitle: '',
      docAuthor: '',
      templateId: '',
      templateStyle: TYPE_TEMPLATE_PRESETS.OPENING.style,
      templateCategory: TYPE_TEMPLATE_PRESETS.OPENING.category,
      styleOptions: PORTAL_STYLES.slice(),
      categoryOptions: PORTAL_CATEGORIES.slice(),
      pptLength: 'medium',
      isCardNote: true,
      isFigure: true,
      templates: [],
      templatesLoading: false,
      errorMessage: '',
      pollTimer: null,
      records: [],
      recordsLoading: false,
      downloadingTaskNo: '',
      entitlement: { ...EMPTY_ENTITLEMENT }
    }
  },
  computed: {
    uploadQuotaHint() {
      if (!this.uploadSession) {
        return ''
      }
      if (this.entitlement.firstFreeAvailable) {
        return '免费生成不扣积分'
      }
      if (this.entitlement.remainingCount > 0) {
        return '本次免费（邀请奖励次数）'
      }
      return ''
    },
    inviteUnlockOnly() {
      return !this.readonly && !this.entitlement.canSubmit && this.entitlement.successCount >= 1
    },
    templateSelected() {
      const id = normalizeTemplateId(this.templateId)
      if (!id) {
        return false
      }
      return this.templates.some(t => normalizeTemplateId(t.templateId) === id)
    },
    formReady() {
      return !this.readonly && !this.uploading && !this.submitting && this.uploadSession && this.templateSelected
    },
    formBlockedHint() {
      if (this.readonly || this.formReady || this.uploading || this.submitting) {
        return ''
      }
      if (!this.uploadSession) {
        return '请先上传文档'
      }
      if (this.templatesLoading) {
        return '模板加载中，请稍候…'
      }
      if (!this.templates.length) {
        return '当前分类下暂无模板，请切换分类、风格或点击刷新'
      }
      if (!this.templateSelected) {
        return '请在下方模板列表中点击一张封面完成选中（必选项）'
      }
      return ''
    },
    canSubmit() {
      if (!this.formReady) {
        return false
      }
      return !!this.entitlement.canSubmit
    },
    canSubmitWithPoints() {
      if (!this.formReady) {
        return false
      }
      return !!this.entitlement.canPayWithPoints
    },
    submitButtonLabel() {
      if (this.submitting) {
        return '提交中…'
      }
      if (this.inviteUnlockOnly) {
        return '去邀请好友解锁'
      }
      return '免费生成PPT'
    }
  },
  mounted() {
    if (!this.readonly) {
      this.applyTypeTemplatePreset(this.pptType)
      this.loadEntitlement()
      this.loadTemplateOptions()
      this.loadTemplates()
      this.loadRecords()
      this.startPolling()
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    loadEntitlement() {
      return getPptEntitlement()
          .then(res => {
            this.entitlement = this.normalizeEntitlement(res.data || {})
          })
          .catch(() => {
            this.entitlement = { ...EMPTY_ENTITLEMENT }
          })
    },
    normalizeEntitlement(raw) {
      const merged = Object.assign({}, EMPTY_ENTITLEMENT, raw)
      const boolKeys = [
        'firstFreeAvailable',
        'showInviteProgress',
        'canSubmit',
        'showPointsPayOption',
        'canPayWithPoints'
      ]
      boolKeys.forEach(key => {
        merged[key] = this.coerceBool(merged[key], EMPTY_ENTITLEMENT[key])
      })
      return merged
    },
    coerceBool(value, fallback) {
      if (value === true || value === false) {
        return value
      }
      if (value === 1 || value === '1' || value === 'true') {
        return true
      }
      if (value === 0 || value === '0' || value === 'false') {
        return false
      }
      return fallback
    },
    displayTitle(item) {
      return item.pptTitle || item.docTitle || item.sourceFileName || item.taskNo
    },
    isProcessing(item) {
      return item && (item.status === '0' || item.status === '1')
    },
    hasActiveRecords() {
      return this.records.some(item => this.isProcessing(item))
    },
    loadTemplateOptions() {
      getPptTemplateOptions()
          .then(res => {
            const data = res.data || {}
            if (data.styles && data.styles.length) {
              this.styleOptions = this.mergeOptions(PORTAL_STYLES, data.styles)
            }
            if (data.categories && data.categories.length) {
              this.categoryOptions = this.mergeOptions(PORTAL_CATEGORIES, data.categories)
            }
          })
          .catch(() => {})
    },
    mergeOptions(base, fromApi) {
      const set = new Set(base)
      fromApi.forEach(item => {
        if (item && item !== '全部') {
          set.add(item)
        }
      })
      return Array.from(set)
    },
    applyTypeTemplatePreset(type) {
      const preset = TYPE_TEMPLATE_PRESETS[type] || TYPE_TEMPLATE_PRESETS.OPENING
      this.templateCategory = preset.category
      this.templateStyle = preset.style
    },
    onPptTypeChange() {
      this.applyTypeTemplatePreset(this.pptType)
      this.templateId = ''
      this.loadTemplates()
    },
    onTemplateFilterChange() {
      this.templateId = ''
      const mapped = CATEGORY_TO_PPT_TYPE[this.templateCategory]
      if (mapped) {
        this.pptType = mapped
      }
      this.loadTemplates()
    },
    isSameTemplateId(a, b) {
      return normalizeTemplateId(a) === normalizeTemplateId(b) && normalizeTemplateId(a) !== ''
    },
    selectTemplate(tpl) {
      const id = normalizeTemplateId(tpl && tpl.templateId)
      if (!id) {
        return
      }
      this.templateId = id
      this.errorMessage = ''
    },
    resolvePptTypeForSubmit() {
      return CATEGORY_TO_PPT_TYPE[this.templateCategory] || this.pptType || 'GRADUATION'
    },
    loadTemplates() {
      this.templatesLoading = true
      const params = { pageNum: 1, pageSize: 24 }
      if (this.templateStyle) {
        params.style = this.templateStyle
      }
      if (this.templateCategory) {
        params.category = this.templateCategory
      }
      getPptTemplates(params)
          .then(res => {
            this.templates = res.data || []
            if (this.templates.length) {
              const current = normalizeTemplateId(this.templateId)
              const stillValid = current && this.templates.some(t => normalizeTemplateId(t.templateId) === current)
              if (!stillValid) {
                const firstId = normalizeTemplateId(this.templates[0].templateId)
                this.templateId = firstId
              }
            } else {
              this.templateId = ''
            }
          })
          .catch(() => {
            this.templates = []
          })
          .finally(() => {
            this.templatesLoading = false
          })
    },
    pickFile() {
      if (this.readonly || this.uploading) {
        if (this.readonly) {
          this.$emit('require-login')
        }
        return
      }
      this.$refs.fileInput && this.$refs.fileInput.click()
    },
    onDragOver() {
      if (!this.readonly && !this.uploading) {
        this.dragOver = true
      }
    },
    onDragLeave() {
      this.dragOver = false
    },
    onDrop(e) {
      this.dragOver = false
      if (this.readonly || this.uploading) {
        if (this.readonly) {
          this.$emit('require-login')
        }
        return
      }
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
      if (file) {
        this.uploadFile(file)
      }
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0]
      if (file) {
        this.uploadFile(file)
      }
      e.target.value = ''
    },
    uploadFile(file) {
      this.errorMessage = ''
      this.uploading = true
      this.uploadingFileName = file.name
      uploadPptFile(file)
          .then(res => {
            this.uploadSession = res.data
            this.loadEntitlement()
          })
          .catch(err => {
            this.errorMessage = err.message || '上传失败'
          })
          .finally(() => {
            this.uploading = false
            this.uploadingFileName = ''
          })
    },
    removeUpload() {
      if (!this.uploadSession || !this.uploadSession.uploadId) {
        this.uploadSession = null
        return
      }
      deletePptUpload(this.uploadSession.uploadId)
          .then(() => {
            this.uploadSession = null
          })
          .catch(err => {
            this.errorMessage = err.message || '删除失败'
          })
    },
    handleSubmit(payWithPoints) {
      if (this.readonly) {
        this.$emit('require-login')
        return
      }
      if (this.submitting) {
        return
      }
      if (payWithPoints) {
        if (!this.uploadSession) {
          this.errorMessage = '请先上传文档'
          return
        }
        if (!this.templateSelected) {
          this.errorMessage = this.formBlockedHint || '请在下方模板列表中点击一张封面完成选中'
          return
        }
        if (!this.entitlement.canPayWithPoints) {
          this.errorMessage = `积分不足，需要 ${this.entitlement.pointsPayCost || 5000} 积分`
          this.$emit('require-recharge')
          return
        }
      } else if (this.inviteUnlockOnly) {
        this.$router.push('/workbench/invite')
        return
      } else {
        if (!this.uploadSession) {
          this.errorMessage = '请先上传文档'
          return
        }
        if (!this.templateSelected) {
          this.errorMessage = this.formBlockedHint || '请在下方模板列表中点击一张封面完成选中'
          return
        }
        if (!this.entitlement.canSubmit) {
          this.errorMessage = this.entitlement.blockMessage || '暂无法免费生成，请先邀请好友或充值积分'
          return
        }
      }
      this.submitting = true
      this.errorMessage = ''
      const payload = {
        uploadId: this.uploadSession.uploadId,
        requestId: buildRequestId(),
        pptType: this.resolvePptTypeForSubmit(),
        isCardNote: this.isCardNote,
        isFigure: this.isFigure,
        pptLength: this.pptLength,
        payWithPoints: !!payWithPoints
      }
      const title = this.docTitle.trim()
      const author = this.docAuthor.trim()
      if (title) {
        payload.docTitle = title
      }
      if (author) {
        payload.docAuthor = author
      }
      payload.templateId = normalizeTemplateId(this.templateId)
      submitPpt(payload)
          .then(() => {
            this.uploadSession = null
            this.docTitle = ''
            this.docAuthor = ''
            this.$emit('ppt-submitted')
            this.loadEntitlement()
            this.loadRecords()
            this.startPolling()
          })
          .catch(err => {
            this.errorMessage = err.message || '提交失败'
          })
          .finally(() => {
            this.submitting = false
          })
    },
    loadRecords() {
      this.recordsLoading = true
      getPptTasks({ page: 1, pageSize: 50 })
          .then(res => {
            this.records = res.data || []
            if (!this.hasActiveRecords()) {
              this.stopPolling()
            } else if (!this.pollTimer) {
              this.startPolling()
            }
          })
          .catch(() => {
            this.records = []
          })
          .finally(() => {
            this.recordsLoading = false
          })
    },
    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(() => {
        if (this.hasActiveRecords()) {
          this.loadRecords()
        } else {
          this.stopPolling()
        }
      }, POLL_INTERVAL)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    downloadPpt(item) {
      if (!item || item.status !== '2') {
        return
      }
      this.downloadingTaskNo = item.taskNo
      getPptDownloadUrl(item.taskNo)
          .then(res => {
            const url = res.data
            if (url) {
              window.open(url, '_blank')
            }
          })
          .catch(err => {
            this.errorMessage = err.message || '获取下载链接失败'
          })
          .finally(() => {
            this.downloadingTaskNo = ''
          })
    },
    deleteRecord(item) {
      if (!item || !item.taskNo) {
        return
      }
      if (!window.confirm('确定删除该记录吗？')) {
        return
      }
      deletePptRecord(item.taskNo)
          .then(() => this.loadRecords())
          .catch(err => {
            this.errorMessage = err.message || '删除失败'
          })
    }
  }
}
</script>

<style scoped>
.ppt-main { display: flex; flex-direction: column; gap: 20px; }
.invite-progress-panel {
  padding: 16px 20px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #bbdefb;
  box-shadow: 0 2px 12px rgba(25, 118, 210, 0.08);
}
.invite-progress-head { display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: 8px; margin-bottom: 12px; }
.invite-progress-title { font-size: 15px; font-weight: 600; color: #1565c0; }
.invite-progress-meta { font-size: 13px; color: #6b7280; }
.invite-progress-cells { display: flex; gap: 10px; margin-bottom: 10px; }
.invite-cell {
  flex: 1;
  max-width: 56px;
  height: 10px;
  border-radius: 999px;
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  transition: background 0.2s ease, box-shadow 0.2s ease;
}
.invite-cell.lit {
  background: linear-gradient(90deg, #0288d1, #1976d2);
  border-color: #1976d2;
  box-shadow: 0 0 8px rgba(25, 118, 210, 0.4);
}
.invite-progress-tip { margin: 0; font-size: 13px; line-height: 1.65; color: #374151; }
.invite-link { margin-left: 6px; color: #1976d2; font-weight: 600; }
.rules-block { margin-top: 12px; padding-top: 0; border-top: none; }
.ppt-workspace { display: grid; grid-template-columns: minmax(0, 2fr) minmax(260px, 1fr); gap: 20px; align-items: stretch; }
.form-panel, .table-panel, .info-panel { border-radius: 14px; background: #fff; border: 1px solid #e5e7eb; }
.form-panel { display: flex; flex-direction: column; padding: 22px 24px; }
.panel-tip { margin: 0; font-size: 13px; color: #6b7280; line-height: 1.65; }
.panel-tip strong { color: #374151; font-weight: 600; }
.compact-form-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 14px;
}
.compact-form-row:first-of-type { margin-top: 14px; }
.compact-label {
  flex-shrink: 0;
  width: 64px;
  padding-top: 7px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  line-height: 1.3;
}
.compact-body { flex: 1; min-width: 0; }
.type-row-hint { margin: 8px 0 0 76px; font-size: 12px; color: #9ca3af; line-height: 1.5; }
.type-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.length-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.length-chip { cursor: pointer; }
.length-chip input { display: none; }
.length-chip span { display: inline-block; padding: 7px 16px; border-radius: 999px; border: 1px solid #e5e7eb; font-size: 13px; color: #374151; white-space: nowrap; }
.length-chip.active span { border-color: #1976d2; background: #e3f2fd; color: #1565c0; font-weight: 600; }
.type-chip { cursor: pointer; }
.type-chip input { display: none; }
.type-chip span { display: inline-block; padding: 7px 14px; border-radius: 999px; border: 1px solid #e5e7eb; font-size: 13px; color: #374151; white-space: nowrap; }
.type-chip.active span { border-color: #1976d2; background: #e3f2fd; color: #1565c0; font-weight: 600; }
.form-row-duo {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
}
.form-row-nested { margin: 0; }
.form-row-nested .form-label { width: 40px; }
.option-compact { margin-top: 4px; align-items: center; }
.option-compact .compact-label { padding-top: 0; }
.option-compact .option-row { display: flex; flex-wrap: wrap; gap: 16px 20px; padding: 0; }
.promo-banner { display: flex; align-items: flex-start; gap: 10px; margin-top: 14px; padding: 12px 14px; border-radius: 10px; font-size: 13px; line-height: 1.6; }
.promo-free { background: #ecfdf5; border: 1px solid #a7f3d0; color: #065f46; }
.promo-free i { color: #059669; font-size: 18px; margin-top: 2px; }
.promo-unlock { align-items: center; justify-content: space-between; flex-wrap: wrap; background: #fffbeb; border: 1px solid #fde68a; color: #92400e; }
.promo-unlock-text { flex: 1; min-width: 200px; }
.promo-unlock-title { margin: 0 0 6px; font-weight: 600; color: #b45309; }
.promo-unlock-desc { margin: 0; font-size: 12px; line-height: 1.65; color: #78350f; }
.promo-unlock-btn { flex-shrink: 0; padding: 8px 16px; border: none; border-radius: 999px; background: #f59e0b; color: #fff; font-size: 13px; font-weight: 600; cursor: pointer; }
.upload-zone { margin-top: 16px; padding: 32px 20px; border: 2px dashed #d1d5db; border-radius: 12px; background: #fafafa; text-align: center; cursor: pointer; }
.upload-zone.dragover, .upload-zone.uploading { border-color: #1976d2; background: #e3f2fd; }
.upload-zone.readonly { cursor: not-allowed; opacity: 0.7; }
.upload-zone.uploading { pointer-events: none; cursor: wait; }
.file-input { display: none; }
.upload-icon { font-size: 28px; color: #1976d2; }
.upload-title { margin: 10px 0 4px; font-size: 15px; }
.upload-link { color: #1976d2; font-weight: 500; }
.upload-hint { margin: 0; font-size: 12px; color: #9ca3af; }
.upload-loading { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.loading-spinner { width: 36px; height: 36px; border: 3px solid #e5e7eb; border-top-color: #1976d2; border-radius: 50%; animation: spin 0.8s linear infinite; }
.upload-loading-title { margin: 0; font-size: 15px; font-weight: 600; color: #1565c0; }
.upload-loading-name { margin: 0; font-size: 13px; color: #374151; }
.upload-info { margin-top: 14px; padding-top: 14px; border-top: 1px dashed #e5e7eb; }
.file-row { display: flex; align-items: center; gap: 12px; }
.file-row > i { font-size: 24px; color: #2563eb; }
.file-meta { flex: 1; min-width: 0; }
.file-name { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.file-sub { margin-top: 4px; font-size: 13px; color: #6b7280; }
.link-btn { border: none; background: none; color: #1976d2; cursor: pointer; }
.form-fields { margin-top: 16px; display: flex; flex-direction: column; gap: 12px; }
.form-row { display: flex; align-items: center; gap: 12px; }
.form-row.custom-row { align-items: flex-start; }
.form-label { flex-shrink: 0; width: 48px; font-size: 14px; color: #374151; }
.form-row.template-section { align-items: flex-start; }
.template-panel { flex: 1; min-width: 0; }
.template-filters { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
.template-filters select { flex: 1; min-width: 120px; padding: 8px 10px; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 13px; }
.ghost-mini { width: 36px; height: 36px; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; color: #6b7280; }
.template-empty-hint { margin: 0 0 8px; font-size: 13px; color: #b45309; line-height: 1.5; }
.template-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(108px, 1fr)); gap: 10px; max-height: 220px; overflow-y: auto; padding: 4px 0; }
.tpl-card { display: flex; flex-direction: column; align-items: stretch; padding: 0; border: 2px solid #e5e7eb; border-radius: 10px; background: #fff; cursor: pointer; overflow: hidden; text-align: left; }
.tpl-card.active { border-color: #1976d2; box-shadow: 0 0 0 1px #1976d2; }
.tpl-cover { width: 100%; aspect-ratio: 16/10; object-fit: cover; background: #f3f4f6; }
.tpl-random { display: flex; align-items: center; justify-content: center; aspect-ratio: 16/10; background: #e3f2fd; color: #1976d2; font-size: 22px; }
.tpl-name { padding: 6px 8px 2px; font-size: 12px; font-weight: 600; color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tpl-meta { padding: 0 8px 8px; font-size: 10px; color: #9ca3af; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.form-row input, .form-row select, .form-row textarea { flex: 1; min-width: 0; padding: 9px 12px; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 14px; }
.form-row textarea { resize: vertical; font-family: inherit; }
.check-item { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: #374151; cursor: pointer; }
.submit-row { margin-top: 16px; display: flex; flex-direction: column; align-items: flex-start; gap: 8px; }
.submit-row-dual { flex-direction: row; flex-wrap: wrap; align-items: center; gap: 12px; }
.points-pay-btn {
  padding: 10px 22px;
  border-radius: 999px;
  border: 1px solid #1976d2;
  background: #fff;
  color: #1565c0;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.points-pay-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.submit-hint-text { margin: 0; font-size: 12px; color: #6b7280; line-height: 1.5; }
.submit-cost-hint { margin: 0; font-size: 13px; color: #6b7280; padding-left: 4px; }
.primary-btn { padding: 10px 28px; border: none; border-radius: 999px; background: var(--primary, #1976d2); color: #fff; font-size: 14px; font-weight: 500; cursor: pointer; }
.primary-btn-paid { background: linear-gradient(135deg, #1976d2, #1565c0); }
.primary-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.warn-text, .error-text { margin: 10px 0 0; font-size: 14px; color: #b91c1c; }
.info-panel { padding: 20px; max-height: min(720px, calc(100vh - 120px)); overflow-y: auto; }
.notice-box { padding: 14px 16px; border-radius: 10px; background: #eff6ff; border: 1px solid #bfdbfe; }
.notice-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; font-size: 14px; font-weight: 600; color: #1d4ed8; }
.guide-lead { margin: 0 0 12px; font-size: 13px; line-height: 1.7; color: #1e3a8a; }
.guide-steps { margin: 0; padding-left: 1.15rem; font-size: 12px; line-height: 1.65; color: #1e40af; }
.guide-steps li { margin-bottom: 8px; }
.guide-steps li:last-child { margin-bottom: 0; }
.guide-block { margin-top: 16px; padding-top: 14px; border-top: 1px solid #e5e7eb; }
.guide-title { display: flex; align-items: center; gap: 8px; margin: 0 0 10px; font-size: 13px; font-weight: 600; color: #374151; }
.guide-title i { color: #1976d2; }
.guide-list { margin: 0; padding-left: 1.1rem; font-size: 12px; line-height: 1.65; color: #6b7280; }
.guide-list li { margin-bottom: 6px; }
.guide-list-tips { list-style: disc; }
.table-panel { padding: 20px 24px; }
.table-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.table-head h3 { margin: 0; font-size: 18px; font-weight: 600; }
.record-table { display: flex; flex-direction: column; gap: 12px; }
.table-row { display: grid; grid-template-columns: 90px 1.4fr 120px 70px 150px 1.2fr; gap: 10px; padding: 14px 12px; border: 1px solid #e5e7eb; border-radius: 10px; background: #fafafa; }
.item-label { font-size: 12px; color: #9ca3af; margin-bottom: 6px; }
.item-value { font-size: 14px; color: #111827; }
.title-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tag { display: inline-flex; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 500; }
.tag-loading { background: #fef3c7; color: #b45309; }
.tag-done { background: #dcfce7; color: #15803d; }
.tag-fail { background: #fef2f2; color: #b91c1c; cursor: help; }
.tag-queue { background: #f3f4f6; color: #6b7280; }
.op-links { display: flex; flex-wrap: wrap; gap: 8px; }
.op-link { display: inline-flex; align-items: center; gap: 4px; border: none; background: none; color: #1976d2; font-size: 13px; cursor: pointer; }
.op-link:disabled { color: #c4c4c4; cursor: not-allowed; }
.op-link.danger { color: #ef4444; }
.empty-text { margin: 0; padding: 24px 0; text-align: center; color: #9ca3af; font-size: 14px; }
.ghost-btn { border: none; background: transparent; color: #6b7280; font-size: 18px; cursor: pointer; }
.spinning { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
@media (max-width: 960px) {
  .ppt-workspace { grid-template-columns: 1fr; }
  .table-row { grid-template-columns: 1fr 1fr; }
  .item.operate { grid-column: 1 / -1; }
  .form-row-duo { grid-template-columns: 1fr; }
  .type-row-hint { margin-left: 0; }
  .compact-form-row { flex-wrap: wrap; }
  .compact-label { width: 100%; padding-top: 0; margin-bottom: 4px; }
}
</style>
